'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { 
  AlertTriangle,
  FileText,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Users,
  Building,
  DollarSign,
  Calendar,
  Shield,
  Flag
} from 'lucide-react'
import type { 
  BirhausSEPRELADDashboardProps,
  SEPRELADReport,
  SEPRELADReportType,
  SEPRELADStatus
} from '../types/compliance'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// SEPRELAD report type configuration
const reportTypeConfig: Record<SEPRELADReportType, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  operacion_sospechosa: {
    icon: <AlertTriangle size={20} />,
    colorClass: 'text-red-600 bg-red-100',
    nameEs: 'Operación Sospechosa',
    nameEn: 'Suspicious Operation'
  },
  operacion_efectivo: {
    icon: <DollarSign size={20} />,
    colorClass: 'text-orange-600 bg-orange-100',
    nameEs: 'Operación en Efectivo',
    nameEn: 'Cash Operation'
  },
  transaccion_umbral: {
    icon: <Flag size={20} />,
    colorClass: 'text-yellow-600 bg-yellow-100',
    nameEs: 'Transacción Umbral',
    nameEn: 'Threshold Transaction'
  },
  actividad_inusual: {
    icon: <AlertTriangle size={20} />,
    colorClass: 'text-purple-600 bg-purple-100',
    nameEs: 'Actividad Inusual',
    nameEn: 'Unusual Activity'
  }
}

// SEPRELAD status configuration
const statusConfig: Record<SEPRELADStatus, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  pendiente: {
    icon: <Clock size={16} />,
    colorClass: 'text-yellow-700 bg-yellow-100',
    nameEs: 'Pendiente',
    nameEn: 'Pending'
  },
  enviado: {
    icon: <Send size={16} />,
    colorClass: 'text-blue-700 bg-blue-100',
    nameEs: 'Enviado',
    nameEn: 'Sent'
  },
  procesado: {
    icon: <CheckCircle size={16} />,
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'Procesado',
    nameEn: 'Processed'
  },
  rechazado: {
    icon: <XCircle size={16} />,
    colorClass: 'text-red-700 bg-red-100',
    nameEs: 'Rechazado',
    nameEn: 'Rejected'
  }
}

/**
 * BirhausSEPRELADDashboard - SEPRELAD compliance dashboard component
 * 
 * Features:
 * - Comprehensive SEPRELAD report management for Paraguay compliance
 * - Real-time status tracking and submission workflow
 * - Multi-language support (Spanish-first for regulatory requirements)
 * - Report type categorization with visual indicators
 * - Search, filter, and export functionality
 * - Regulatory deadline tracking and alerts
 * - Person/Entity information management
 * - Risk assessment integration
 * - Document attachment support
 * - Miller's Law compliance (max 7 visible reports per view)
 */
export function BirhausSEPRELADDashboard({
  reportes = [],
  onCreateReport,
  onViewReport,
  onSubmitReport,
  showCreateButton = true,
  className,
  ...props
}: BirhausSEPRELADDashboardProps) {
  const { language, reportViolation } = useBirhaus()
  const [searchTerm, setSearchTerm] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState<SEPRELADStatus | 'all'>('all')
  const [filterType, setFilterType] = React.useState<SEPRELADReportType | 'all'>('all')

  // BIRHAUS validation: Check cognitive load (max 7 reports - Miller's Law)
  React.useEffect(() => {
    if (reportes.length > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('div'),
        message: `SEPRELAD dashboard shows ${reportes.length} reports (max recommended: 7)`,
        messageEs: `Panel SEPRELAD muestra ${reportes.length} reportes (máximo recomendado: 7)`,
        messageEn: `SEPRELAD dashboard shows ${reportes.length} reports (max recommended: 7)`,
        recommendation: 'Use pagination or filtering to reduce cognitive load',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [reportes.length, reportViolation])

  // Filter reports based on search and filters
  const filteredReports = React.useMemo(() => {
    let filtered = reportes

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(report =>
        report.numeroReporte.toLowerCase().includes(term) ||
        report.descripcionOperacion.toLowerCase().includes(term) ||
        report.entidadReportante.toLowerCase().includes(term) ||
        report.personaFisica?.nombres.toLowerCase().includes(term) ||
        report.personaFisica?.apellidos.toLowerCase().includes(term) ||
        report.personaJuridica?.razonSocial.toLowerCase().includes(term)
      )
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(report => report.estado === filterStatus)
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(report => report.tipo === filterType)
    }

    return filtered.sort((a, b) => new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime())
  }, [reportes, searchTerm, filterStatus, filterType])

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    const total = filteredReports.length
    const pending = filteredReports.filter(r => r.estado === 'pendiente').length
    const sent = filteredReports.filter(r => r.estado === 'enviado').length
    const processed = filteredReports.filter(r => r.estado === 'procesado').length
    const rejected = filteredReports.filter(r => r.estado === 'rechazado').length
    
    // Calculate total USD amount
    const totalUSD = filteredReports.reduce((sum, report) => sum + report.montoUSD, 0)
    
    // High-risk reports
    const highRisk = filteredReports.filter(r => r.nivelRiesgo === 'alto').length
    
    return {
      total,
      pending,
      sent,
      processed,
      rejected,
      totalUSD,
      highRisk
    }
  }, [filteredReports])

  // Handle actions
  const handleCreateReport = React.useCallback(() => {
    onCreateReport?.()
  }, [onCreateReport])

  const handleViewReport = React.useCallback((report: SEPRELADReport) => {
    onViewReport?.(report)
  }, [onViewReport])

  const handleSubmitReport = React.useCallback((reportId: string) => {
    onSubmitReport?.(reportId)
  }, [onSubmitReport])

  // Check if report is overdue
  const isOverdue = React.useCallback((report: SEPRELADReport) => {
    const daysSinceCreation = Math.floor(
      (Date.now() - report.fechaCreacion.getTime()) / (1000 * 60 * 60 * 24)
    )
    // SEPRELAD requires reporting within 15 days for suspicious operations
    return daysSinceCreation > 15 && report.estado === 'pendiente'
  }, [])

  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-birhaus-foreground">
            {language === 'es' ? 'Panel SEPRELAD' : 'SEPRELAD Dashboard'}
          </h1>
          <p className="text-birhaus-muted-foreground">
            {language === 'es' 
              ? 'Gestión de reportes de cumplimiento normativo paraguayo'
              : 'Paraguay regulatory compliance report management'
            }
          </p>
        </div>
        
        {showCreateButton && onCreateReport && (
          <button
            onClick={handleCreateReport}
            className="flex items-center gap-2 px-4 py-2 bg-birhaus-primary text-white rounded-lg hover:bg-birhaus-primary/90 transition-colors"
          >
            <Plus size={16} />
            <span>{language === 'es' ? 'Nuevo Reporte' : 'New Report'}</span>
          </button>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 border border-birhaus-border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 text-blue-600 rounded-full">
              <FileText size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-birhaus-foreground">{summaryStats.total}</p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Total Reportes' : 'Total Reports'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 border border-birhaus-border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 text-yellow-600 rounded-full">
              <Clock size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-birhaus-foreground">{summaryStats.pending}</p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Pendientes' : 'Pending'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 border border-birhaus-border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 text-red-600 rounded-full">
              <Shield size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-birhaus-foreground">{summaryStats.highRisk}</p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Alto Riesgo' : 'High Risk'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 border border-birhaus-border rounded-lg">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 text-green-600 rounded-full">
              <DollarSign size={20} />
            </div>
            <div>
              <p className="text-lg font-bold text-birhaus-foreground">
                ${summaryStats.totalUSD.toLocaleString()} USD
              </p>
              <p className="text-sm text-birhaus-muted-foreground">
                {language === 'es' ? 'Monto Total' : 'Total Amount'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-birhaus-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={language === 'es' ? 'Buscar reportes...' : 'Search reports...'}
            className="w-full pl-10 pr-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary focus:border-birhaus-primary"
          />
        </div>

        {/* Status Filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as SEPRELADStatus | 'all')}
          className="px-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
        >
          <option value="all">{language === 'es' ? 'Todos los Estados' : 'All Statuses'}</option>
          {Object.entries(statusConfig).map(([key, config]) => (
            <option key={key} value={key}>
              {language === 'es' ? config.nameEs : config.nameEn}
            </option>
          ))}
        </select>

        {/* Type Filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as SEPRELADReportType | 'all')}
          className="px-4 py-2 border border-birhaus-border rounded-lg focus:outline-none focus:ring-2 focus:ring-birhaus-primary"
        >
          <option value="all">{language === 'es' ? 'Todos los Tipos' : 'All Types'}</option>
          {Object.entries(reportTypeConfig).map(([key, config]) => (
            <option key={key} value={key}>
              {language === 'es' ? config.nameEs : config.nameEn}
            </option>
          ))}
        </select>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {filteredReports.length === 0 ? (
          <div className="text-center py-12 bg-white border border-birhaus-border rounded-lg">
            <FileText size={48} className="mx-auto mb-4 text-birhaus-muted-foreground/50" />
            <h3 className="text-lg font-semibold text-birhaus-foreground mb-2">
              {language === 'es' ? 'No hay reportes' : 'No reports found'}
            </h3>
            <p className="text-birhaus-muted-foreground mb-4">
              {searchTerm || filterStatus !== 'all' || filterType !== 'all' ? (
                language === 'es' 
                  ? 'No se encontraron reportes con los filtros aplicados'
                  : 'No reports found with applied filters'
              ) : (
                language === 'es'
                  ? 'Crea tu primer reporte SEPRELAD'
                  : 'Create your first SEPRELAD report'
              )}
            </p>
            {showCreateButton && onCreateReport && (
              <button
                onClick={handleCreateReport}
                className="inline-flex items-center gap-2 px-4 py-2 bg-birhaus-primary text-white rounded-lg hover:bg-birhaus-primary/90 transition-colors"
              >
                <Plus size={16} />
                <span>{language === 'es' ? 'Crear Reporte' : 'Create Report'}</span>
              </button>
            )}
          </div>
        ) : (
          filteredReports.map((report) => {
            const typeConfig = reportTypeConfig[report.tipo]
            const statusInfo = statusConfig[report.estado]
            const overdue = isOverdue(report)
            const subject = report.personaFisica 
              ? `${report.personaFisica.nombres} ${report.personaFisica.apellidos}`
              : report.personaJuridica?.razonSocial || 'N/A'

            return (
              <div
                key={report.id}
                className={cn(
                  'bg-white p-6 border rounded-lg transition-all duration-200 hover:shadow-md',
                  overdue ? 'border-red-300 bg-red-50' : 'border-birhaus-border',
                  onViewReport && 'cursor-pointer hover:border-birhaus-primary/50'
                )}
                onClick={() => onViewReport && handleViewReport(report)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div className={cn('p-3 rounded-full', typeConfig.colorClass)}>
                      {typeConfig.icon}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-birhaus-foreground">
                          {report.numeroReporte}
                        </h3>
                        <span className={cn(
                          'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full',
                          statusInfo.colorClass
                        )}>
                          {statusInfo.icon}
                          <span>{language === 'es' ? statusInfo.nameEs : statusInfo.nameEn}</span>
                        </span>
                        
                        {overdue && (
                          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full text-red-700 bg-red-100">
                            <AlertTriangle size={12} />
                            <span>{language === 'es' ? 'Vencido' : 'Overdue'}</span>
                          </span>
                        )}
                      </div>
                      
                      <p className="text-sm text-birhaus-muted-foreground mb-2">
                        {language === 'es' ? typeConfig.nameEs : typeConfig.nameEn}
                      </p>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Sujeto' : 'Subject'}
                          </p>
                          <p className="font-medium flex items-center gap-1">
                            {report.personaFisica ? <Users size={12} /> : <Building size={12} />}
                            {subject}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Monto USD' : 'USD Amount'}
                          </p>
                          <p className="font-medium">
                            ${report.montoUSD.toLocaleString()}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Nivel de Riesgo' : 'Risk Level'}
                          </p>
                          <p className={cn(
                            'font-medium',
                            report.nivelRiesgo === 'alto' && 'text-red-600',
                            report.nivelRiesgo === 'medio' && 'text-yellow-600',
                            report.nivelRiesgo === 'bajo' && 'text-green-600'
                          )}>
                            {report.nivelRiesgo.charAt(0).toUpperCase() + report.nivelRiesgo.slice(1)}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-birhaus-muted-foreground mb-1">
                            {language === 'es' ? 'Fecha Creación' : 'Created'}
                          </p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar size={12} />
                            {report.fechaCreacion.toLocaleDateString(language === 'es' ? 'es-PY' : 'en-US')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex items-center gap-2">
                    {report.estado === 'pendiente' && onSubmitReport && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSubmitReport(report.id)
                        }}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                      >
                        <Send size={12} />
                        <span>{language === 'es' ? 'Enviar' : 'Submit'}</span>
                      </button>
                    )}
                    
                    {onViewReport && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewReport(report)
                        }}
                        className="p-2 text-birhaus-muted-foreground hover:text-birhaus-foreground hover:bg-birhaus-muted rounded transition-colors"
                        aria-label={language === 'es' ? 'Ver reporte' : 'View report'}
                      >
                        <Eye size={16} />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-sm text-birhaus-foreground mb-3 line-clamp-2">
                  {report.descripcionOperacion}
                </p>
                
                {/* Risk indicators */}
                {report.indicadoresRiesgo.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-xs text-birhaus-muted-foreground">
                      {language === 'es' ? 'Indicadores:' : 'Indicators:'}
                    </span>
                    {report.indicadoresRiesgo.slice(0, 3).map((indicator, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded"
                      >
                        {indicator}
                      </span>
                    ))}
                    {report.indicadoresRiesgo.length > 3 && (
                      <span className="px-2 py-1 text-xs bg-birhaus-muted text-birhaus-muted-foreground rounded">
                        +{report.indicadoresRiesgo.length - 3}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Timeline */}
                <div className="flex items-center gap-4 text-xs text-birhaus-muted-foreground">
                  <span>
                    {language === 'es' ? 'Creado por:' : 'Created by:'} {report.creadoPor}
                  </span>
                  {report.fechaEnvio && (
                    <span>
                      {language === 'es' ? 'Enviado:' : 'Sent:'} {report.fechaEnvio.toLocaleDateString()}
                    </span>
                  )}
                  {report.fechaProcesamiento && (
                    <span>
                      {language === 'es' ? 'Procesado:' : 'Processed:'} {report.fechaProcesamiento.toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export type { BirhausSEPRELADDashboardProps }